/**
 * GRANULLO PLATFORM EVOLUTION — Monitoring Engine Context
 * Phase 4: Monitoring + Exposure + Cascade Integration
 * 
 * FOUNDER DIRECTIVE:
 * Monitoring Engine must evaluate live and expected system behavior against canonical baselines.
 * It must operate on signal definitions already preserved in the Program Object.
 * 
 * Monitoring outputs must include:
 * - signal health state
 * - drift severity
 * - node-level anomalies
 * - unresolved alert backlog
 * - trend direction where applicable
 * 
 * Monitoring must not infer arbitrary signals outside the defined signal schema.
 * Monitoring must remain governance-relevant, not decorative analytics.
 * 
 * ARCHITECTURE CONSTRAINT: Consumes canonical Blueprint Graph and Baseline Signals only.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { THRESHOLD_LEVELS, evaluateSignalDrift } from './BaselineSignalsContext';

// ============================================================================
// MONITORING TYPES
// ============================================================================

export const MONITORING_STATUS = {
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  CRITICAL: 'critical',
  OFFLINE: 'offline'
};

export const TREND_DIRECTION = {
  IMPROVING: 'improving',
  STABLE: 'stable',
  DEGRADING: 'degrading',
  UNKNOWN: 'unknown'
};

export const ANOMALY_TYPES = {
  SPIKE: 'spike',
  DROP: 'drop',
  SUSTAINED_DEVIATION: 'sustained_deviation',
  PATTERN_BREAK: 'pattern_break',
  THRESHOLD_BREACH: 'threshold_breach'
};

// ============================================================================
// MONITORING ENGINE FUNCTIONS
// ============================================================================

/**
 * Evaluate signal health state
 * Returns health status based on current value vs baseline
 */
export function evaluateSignalHealth(signal, currentValue) {
  if (currentValue === null || currentValue === undefined) {
    return { status: MONITORING_STATUS.OFFLINE, score: 0 };
  }
  
  const baseline = signal.normal_range || signal.baseline;
  if (!baseline) return { status: MONITORING_STATUS.OFFLINE, score: 0 };
  
  const { min, max, target } = baseline;
  const range = max - min;
  const deviation = Math.abs(currentValue - target);
  const deviationPercent = range > 0 ? (deviation / range) : 0;
  
  // Determine health status
  let status = MONITORING_STATUS.HEALTHY;
  let score = 100;
  
  if (deviationPercent >= 0.9) {
    status = MONITORING_STATUS.CRITICAL;
    score = 10;
  } else if (deviationPercent >= 0.5) {
    status = MONITORING_STATUS.DEGRADED;
    score = 50 - (deviationPercent - 0.5) * 80;
  } else if (deviationPercent >= 0.25) {
    status = MONITORING_STATUS.DEGRADED;
    score = 75 - (deviationPercent - 0.25) * 100;
  } else {
    score = 100 - deviationPercent * 100;
  }
  
  return {
    status,
    score: Math.max(0, Math.min(100, Math.round(score))),
    deviationPercent: Math.round(deviationPercent * 100),
    isWithinBounds: currentValue >= min && currentValue <= max
  };
}

/**
 * Calculate drift severity from signal evaluation
 */
export function calculateDriftSeverity(signals) {
  if (!signals || signals.length === 0) {
    return { severity: 'none', score: 0, driftingSignals: 0 };
  }
  
  const driftingSignals = signals.filter(s => 
    s.currentStatus && s.currentStatus !== THRESHOLD_LEVELS.NORMAL
  );
  
  const catastrophicCount = signals.filter(s => s.currentStatus === THRESHOLD_LEVELS.CATASTROPHIC).length;
  const emergencyCount = signals.filter(s => s.currentStatus === THRESHOLD_LEVELS.EMERGENCY).length;
  const criticalCount = signals.filter(s => s.currentStatus === THRESHOLD_LEVELS.CRITICAL).length;
  const warningCount = signals.filter(s => s.currentStatus === THRESHOLD_LEVELS.WARNING).length;
  
  // Weighted severity score
  const severityScore = (catastrophicCount * 100 + emergencyCount * 75 + criticalCount * 50 + warningCount * 25) / signals.length;
  
  let severity = 'none';
  if (catastrophicCount > 0) severity = 'catastrophic';
  else if (emergencyCount > 0) severity = 'emergency';
  else if (criticalCount > 0) severity = 'critical';
  else if (warningCount > 0) severity = 'warning';
  
  return {
    severity,
    score: Math.round(severityScore),
    driftingSignals: driftingSignals.length,
    totalSignals: signals.length,
    breakdown: { catastrophic: catastrophicCount, emergency: emergencyCount, critical: criticalCount, warning: warningCount }
  };
}

/**
 * Detect node-level anomalies from signals attached to nodes
 */
export function detectNodeAnomalies(signals, blueprintGraph) {
  const anomalies = [];
  
  if (!signals || !blueprintGraph?.nodes) return anomalies;
  
  // Group signals by node
  const signalsByNode = {};
  signals.forEach(signal => {
    const nodeId = signal.nodeRef || signal.node_scope;
    if (nodeId) {
      if (!signalsByNode[nodeId]) signalsByNode[nodeId] = [];
      signalsByNode[nodeId].push(signal);
    }
  });
  
  // Check each node for anomalies
  Object.entries(signalsByNode).forEach(([nodeId, nodeSignals]) => {
    const node = blueprintGraph.nodes.find(n => n.id === nodeId);
    const nodeName = node?.name || nodeId;
    
    // Check for threshold breaches
    const breachedSignals = nodeSignals.filter(s => 
      s.currentStatus && s.currentStatus !== THRESHOLD_LEVELS.NORMAL
    );
    
    if (breachedSignals.length > 0) {
      const maxSeverity = breachedSignals.reduce((max, s) => {
        const severityOrder = { catastrophic: 4, emergency: 3, critical: 2, warning: 1, normal: 0 };
        return severityOrder[s.currentStatus] > severityOrder[max] ? s.currentStatus : max;
      }, 'normal');
      
      anomalies.push({
        id: `anomaly_${nodeId}_${Date.now()}`,
        type: ANOMALY_TYPES.THRESHOLD_BREACH,
        nodeId,
        nodeName,
        severity: maxSeverity,
        affectedSignals: breachedSignals.map(s => s.signal_id || s.id),
        description: `${breachedSignals.length} signal(s) breached threshold at ${nodeName}`,
        detectedAt: new Date().toISOString()
      });
    }
  });
  
  return anomalies;
}

/**
 * Calculate trend direction based on signal history
 */
export function calculateTrendDirection(signalHistory) {
  if (!signalHistory || signalHistory.length < 2) {
    return TREND_DIRECTION.UNKNOWN;
  }
  
  // Compare recent values to older values
  const recentValues = signalHistory.slice(-3);
  const olderValues = signalHistory.slice(-6, -3);
  
  if (olderValues.length === 0) return TREND_DIRECTION.UNKNOWN;
  
  const recentAvg = recentValues.reduce((sum, v) => sum + (v.value || 0), 0) / recentValues.length;
  const olderAvg = olderValues.reduce((sum, v) => sum + (v.value || 0), 0) / olderValues.length;
  
  const changePercent = olderAvg !== 0 ? ((recentAvg - olderAvg) / olderAvg) * 100 : 0;
  
  if (Math.abs(changePercent) < 5) return TREND_DIRECTION.STABLE;
  // Note: For health metrics, higher is usually better
  return changePercent > 0 ? TREND_DIRECTION.IMPROVING : TREND_DIRECTION.DEGRADING;
}

// ============================================================================
// CONTEXT
// ============================================================================

const MonitoringEngineContext = createContext(null);

export function MonitoringEngineProvider({ children }) {
  // Engine state
  const [monitoringState, setMonitoringState] = useState({
    status: MONITORING_STATUS.HEALTHY,
    lastScanAt: null,
    scanInterval: 60000, // 1 minute default
    isScanning: false
  });
  
  // Signal states (derived from canonical signals)
  const [signalStates, setSignalStates] = useState(new Map());
  const [nodeAnomalies, setNodeAnomalies] = useState([]);
  const [alertBacklog, setAlertBacklog] = useState([]);
  const [driftSeverity, setDriftSeverity] = useState({ severity: 'none', score: 0 });
  
  // Blueprint and signals references (set by integration)
  const [blueprintGraph, setBlueprintGraph] = useState(null);
  const [baselineSignals, setBaselineSignals] = useState([]);
  
  /**
   * Set canonical data sources
   */
  const setDataSources = useCallback(({ blueprint, signals }) => {
    if (blueprint) setBlueprintGraph(blueprint);
    if (signals) setBaselineSignals(signals);
  }, []);
  
  /**
   * Execute monitoring scan
   * Evaluates all signals against their baselines
   */
  const executeScan = useCallback((currentValues = {}) => {
    setMonitoringState(prev => ({ ...prev, isScanning: true }));
    
    const newSignalStates = new Map();
    
    baselineSignals.forEach(signal => {
      const signalId = signal.signal_id || signal.id;
      const currentValue = currentValues[signalId] ?? signal.currentValue;
      
      const health = evaluateSignalHealth(signal, currentValue);
      const drift = currentValue !== null ? evaluateSignalDrift(signal, currentValue) : null;
      
      newSignalStates.set(signalId, {
        signalId,
        signalName: signal.name,
        category: signal.category,
        nodeRef: signal.nodeRef || signal.node_scope,
        currentValue,
        health,
        drift,
        lastEvaluatedAt: new Date().toISOString()
      });
    });
    
    setSignalStates(newSignalStates);
    
    // Calculate drift severity
    const signalsArray = Array.from(newSignalStates.values()).map(s => ({
      ...s,
      currentStatus: s.drift?.status || THRESHOLD_LEVELS.NORMAL
    }));
    const newDriftSeverity = calculateDriftSeverity(signalsArray);
    setDriftSeverity(newDriftSeverity);
    
    // Detect node anomalies
    const anomalies = detectNodeAnomalies(signalsArray, blueprintGraph);
    setNodeAnomalies(anomalies);
    
    // Update overall monitoring status
    let overallStatus = MONITORING_STATUS.HEALTHY;
    if (newDriftSeverity.severity === 'catastrophic') overallStatus = MONITORING_STATUS.CRITICAL;
    else if (newDriftSeverity.severity === 'emergency' || newDriftSeverity.severity === 'critical') overallStatus = MONITORING_STATUS.DEGRADED;
    else if (newDriftSeverity.severity === 'warning') overallStatus = MONITORING_STATUS.DEGRADED;
    
    setMonitoringState(prev => ({
      ...prev,
      status: overallStatus,
      lastScanAt: new Date().toISOString(),
      isScanning: false
    }));
    
    return {
      status: overallStatus,
      signalStates: newSignalStates,
      driftSeverity: newDriftSeverity,
      anomalies
    };
  }, [baselineSignals, blueprintGraph]);
  
  /**
   * Add alert to backlog
   */
  const addAlert = useCallback((alert) => {
    setAlertBacklog(prev => [...prev, {
      ...alert,
      id: alert.id || `alert_${Date.now()}`,
      createdAt: new Date().toISOString(),
      acknowledged: false
    }]);
  }, []);
  
  /**
   * Acknowledge alert
   */
  const acknowledgeAlert = useCallback((alertId) => {
    setAlertBacklog(prev => prev.map(a => 
      a.id === alertId ? { ...a, acknowledged: true, acknowledgedAt: new Date().toISOString() } : a
    ));
  }, []);
  
  /**
   * Clear resolved alerts
   */
  const clearResolvedAlerts = useCallback(() => {
    setAlertBacklog(prev => prev.filter(a => !a.acknowledged));
  }, []);
  
  // Computed values
  const monitoringSummary = useMemo(() => {
    const totalSignals = signalStates.size;
    const healthySignals = Array.from(signalStates.values()).filter(s => s.health?.status === MONITORING_STATUS.HEALTHY).length;
    const degradedSignals = Array.from(signalStates.values()).filter(s => s.health?.status === MONITORING_STATUS.DEGRADED).length;
    const criticalSignals = Array.from(signalStates.values()).filter(s => s.health?.status === MONITORING_STATUS.CRITICAL).length;
    const offlineSignals = Array.from(signalStates.values()).filter(s => s.health?.status === MONITORING_STATUS.OFFLINE).length;
    
    const healthScore = totalSignals > 0 
      ? Math.round((healthySignals / totalSignals) * 100)
      : 100;
    
    return {
      totalSignals,
      healthySignals,
      degradedSignals,
      criticalSignals,
      offlineSignals,
      healthScore,
      anomalyCount: nodeAnomalies.length,
      unresolvedAlerts: alertBacklog.filter(a => !a.acknowledged).length,
      status: monitoringState.status,
      lastScanAt: monitoringState.lastScanAt
    };
  }, [signalStates, nodeAnomalies, alertBacklog, monitoringState]);
  
  const value = {
    // Constants
    MONITORING_STATUS,
    TREND_DIRECTION,
    ANOMALY_TYPES,
    
    // State
    monitoringState,
    signalStates,
    nodeAnomalies,
    alertBacklog,
    driftSeverity,
    
    // Computed
    monitoringSummary,
    
    // Actions
    setDataSources,
    executeScan,
    addAlert,
    acknowledgeAlert,
    clearResolvedAlerts,
    
    // Utility functions
    evaluateSignalHealth,
    calculateDriftSeverity,
    detectNodeAnomalies,
    calculateTrendDirection
  };
  
  return (
    <MonitoringEngineContext.Provider value={value}>
      {children}
    </MonitoringEngineContext.Provider>
  );
}

export function useMonitoringEngine() {
  const context = useContext(MonitoringEngineContext);
  if (!context) {
    throw new Error('useMonitoringEngine must be used within MonitoringEngineProvider');
  }
  return context;
}

export default MonitoringEngineContext;
